/* eslint-disable max-len */
import React, { useCallback, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { InfoView } from '../../ui/InfoView';
import { InlineHelp } from '../../ui/InlineHelp';
import { ResultInfo } from '../../ui/ResultInfo';
import { FadeIn } from '../../ui/Transitions';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { EnableIfHasFeature, FeatureCallout, HideIfHasFeature } from '../common/FeatureElements';
import { ButtonStrip, Copy, DisableFrame, Em, Flexer, Spacer, Title, ToggleFrame, ToggleLabel } from '../dashboard/elements/LayoutElements';
import { UpgradeButton } from '../upgrade/UpgradeButton';
import { Button, Input, Toggle, remInPx } from './../../ui';
import { ManageUserAccessControlModel, ManageUserAccessControlScope } from './ManageUserAccessControlScope';

const { Section, LargeHeading, } = InfoView;

const upgradeMessage = `
    In order to enable Access Control, your organization must be a subscriber to either our Pro or Business plans.
    Under the Free Plan, any person is able to join your organization if they are provided a link to a board or workspace.
`;

export const ManageUserAccessControlLayout = () => {

    const api = useResolve(ApiClient);
    const account = useResolve(AccountModel);

    const [working, setWorking] = useState(false);
    const [updated, setUpdated] = useState(0);
    const form = useResolve(ManageUserAccessControlModel);
    useObserver(form);

    const checkErrors = useCallback(() => {
        const results = [
            // ['entryPoint', !sso.entryPoint?.trim()],
            // ['issuer', !sso.issuer?.trim()],
            // ['certificate', !sso.certificate?.trim()],
        ];
        // setErrors(Object.fromEntries(results));
        return !!results.find(x => !!x[1]);
    }, []);

    const onSave = useCallback(async () => {
        if (checkErrors()) return;
        setWorking(true);
        form.format();
        const [_, ok] = await api.execs(v2.admin.accessControl.save(form.getState()));
        setWorking(false);
        setUpdated(x => ok ? Math.max(0, x) + 1 : Math.min(0, x) - 1);
    }, [form, checkErrors]);

    return <>
        <Section>
            <LargeHeading>Access Control</LargeHeading>
        </Section>
        <Section>
            <Copy>
                By default, all users need to approved before joining <Em>{account.label}</Em>.
                If you would like to streamline access, you can whitelist your company&apos;s email domain(s) so
                that anyone who signs up with a confirmed email address at that domain will automatically be approved.
            </Copy>
            <Spacer f={2} />
            <ToggleFrame>
                <Toggle value={form.autoJoinEnabled} onAction={x => form.update({ autoJoinEnabled: x })} />
                <ToggleLabel>Auto-approve anyone with a confirmed email address from a whitelisted domain to join {account.label}?</ToggleLabel>
                {/* <ToggleHelp>
                    <HelpTip content={restrictHelpText} />
                </ToggleHelp> */}
            </ToggleFrame>
            <Spacer f={2} />
            <DisableFrame disabled={!form.autoJoinEnabled}>
                <Input label="Whitelist" placeholder="e.g. *@acme.com"
                    multiline={{ rows: 5, resize: 'none' }}
                    hint="The * character acts as a wildcard."
                    value={form.autoJoinWhitelist}
                    onChange={x => form.update({ autoJoinWhitelist: x })} />
            </DisableFrame>
            <Spacer f={3} />
            <InlineHelp variant="secondary">
                <Title>How is this different to Single Sign-On?</Title>
                <Copy>
                    Single Sign-On allows you to manage access to Metro Retro from an Identity Provider (IDP) such as Okta.
                    The settings above control how people can join your Metro Retro organization directly when someone shares them a link.
                </Copy>
                <Copy>
                    If you have SSO configured with Restrict login to Single Sign-On enabled, this overrides the Access Control settings.
                    Otherwise they work side by side.
                </Copy>
            </InlineHelp>
            <Spacer f={2} />
            <ButtonStrip>
                <Button disabled={working} variant="primary" onAction={onSave}>
                    {working ? 'Saving...' : 'Save Changes'}
                    {working && <Loader
                        type="TailSpin"
                        color="#fff"
                        height={remInPx(1.2)}
                        width={remInPx(1.2)}
                    />}
                </Button>
            </ButtonStrip>
            <Spacer f={2} />
            {updated > 0 && <ResultInfo key={updated} mode="positive" message="Access control configuration updated." />}
            {updated < 0 && <ResultInfo key={updated} mode="negative" message="Access control configuration failed to update.  Please contact support." />}
        </Section>
    </>;
};

export const ManageUserAccessControlView = () =>
    <ManageUserAccessControlScope>
        <FadeIn>
            <Flexer maxWidth="64">
                <HideIfHasFeature name="accessControl">
                    <FeatureCallout margin="2 2 1 2">
                        <Title>This is a Premium feature.</Title>
                        <Copy>
                            In order to enable Access Control, your organization must be a subscriber to one of our paid plans.
                            Under the Free Plan, any person is able to join your organization if they are provided a link to a board or workspace.
                        </Copy>
                        <UpgradeButton variant="common" />
                    </FeatureCallout>
                </HideIfHasFeature>
                <EnableIfHasFeature name="accessControl">
                    <ManageUserAccessControlLayout />
                </EnableIfHasFeature>
            </Flexer>
        </FadeIn>
    </ManageUserAccessControlScope>;
